import { useState, useRef, useCallback } from 'react';
import { convertToWav } from './utils';

// ════════════════════════════════════════════════════════
//  useAudioRecorder — универсальный хук для записи аудио
//  Заменяет 3 идентичных копии (initial / update / clarification)
// ════════════════════════════════════════════════════════

/**
 * @param {function} [onError] — коллбэк при ошибке доступа к микрофону
 * @returns {{
 *   isRecording: boolean,
 *   audioBlob: Blob|null,
 *   audioUrl: string|null,
 *   recordingRef: React.MutableRefObject<boolean>,
 *   startRecording: function,
 *   stopRecording: function,
 *   deleteRecording: function,
 * }}
 */
export default function useAudioRecorder(onError) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const recordingRef = useRef(false);
  const audioBlobRef = useRef(null);

  const startRecording = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (recordingRef.current || audioBlobRef.current) return;
    recordingRef.current = true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Пользователь отпустил кнопку до получения разрешения
      if (!recordingRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];

      mediaRecorder.ondataavailable = (evt) => {
        if (evt.data.size > 0) chunks.push(evt.data);
      };

      mediaRecorder.onstop = async () => {
        // Если уже начата новая запись — игнорируем старый результат
        if (mediaRecorderRef.current !== mediaRecorder) return;
        try {
          const rawBlob = new Blob(chunks, { type: mediaRecorder.mimeType || 'audio/webm' });
          const wavBlob = await convertToWav(rawBlob);
          audioBlobRef.current = wavBlob;
          setAudioBlob(wavBlob);
          setAudioUrl(URL.createObjectURL(wavBlob));
        } catch (err) {
          console.error('Error converting audio to WAV:', err);
          onError?.('Ошибка при обработке аудио. Попробуйте еще раз.');
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      onError?.('Не удалось получить доступ к микрофону. Проверьте разрешения браузера.');
      recordingRef.current = false;
    }
  }, [onError]);

  const stopRecording = useCallback((e) => {
    if (e) e.preventDefault();
    recordingRef.current = false;
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      mr.stop();
      setIsRecording(false);
      mr.stream.getTracks().forEach((t) => t.stop());
    }
  }, []);

  const deleteRecording = useCallback(() => {
    audioBlobRef.current = null;
    setAudioBlob(null);
    setIsRecording(false);
    recordingRef.current = false;
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  return {
    isRecording,
    audioBlob,
    audioUrl,
    recordingRef,
    startRecording,
    stopRecording,
    deleteRecording,
  };
}
